import { useEffect, useState } from "react";
import { useCart } from "@nusrif/contexts";
import { useNavigate } from "react-router";
import { PaymentHeader } from "./PaymentHeader";
import { OrderSummary } from "./OrderSummary";
import { PaymentButton } from "./PaymentButton";
import { DeliveryAddressCard } from "./DeliveryAddressCard";
import { useCashfreeSDK } from "@nusrif/hooks";
import { usePaymentHandler } from "@nusrif/hooks";
import { trackEvent } from "@nusrif/services";
import { ApiCartAddress, AppliedCoupon } from "@nusrif/types";
import { Spinner } from "../ui/Spinner";

export const PaymentPage = () => {
  const { state } = useCart();
  const navigate = useNavigate();
  const [deliveryAddress, setDeliveryAddress] = useState<ApiCartAddress | null>(
    null
  );
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(
    null
  );
  const cashfree = useCashfreeSDK();
  const { handlePayment, isProcessing, paymentStarted, isVerifying } =
    usePaymentHandler(cashfree, state.cart?.id);

  useEffect(() => {
    const coupon = localStorage.getItem("appliedCoupon");
    if (state.cart?.shippingAddress)
      setDeliveryAddress(state.cart?.shippingAddress);
    if (coupon) setAppliedCoupon(JSON.parse(coupon));
    if (!state.cart || state.cart.lines.length === 0) {
      navigate("/cart");
    }
  }, [state.cart, navigate]);

  const calculateFinalTotal = () => {
    if (!state.cart) return 0;

    let total = state.cart.totalWithTax;
    if (appliedCoupon) {
      const discount =
        appliedCoupon.type === "percentage"
          ? (state.cart.subTotalWithTax * appliedCoupon.discount) / 100
          : appliedCoupon.discount;
      total -= discount;
    }
    return Math.max(0, total);
  };

  const finalTotal = calculateFinalTotal();

  const handlePaymentWithTracking = async () => {
    // Track payment initiation
    trackEvent({
      event: "payment_initiated",
      properties: {
        cart_id: state.cart?.id || "",
        payment_method: "card", // Default for now, can be dynamic
        final_total: finalTotal,
        cart_subtotal: state.cart?.subTotalWithTax || 0,
        delivery_fee: state.cart?.shippingWithTax || 0,
        item_count:
          state.cart?.lines.reduce((sum, line) => sum + line.quantity, 0) || 0,
        unique_items: state.cart?.lines.length || 0,
        has_coupon: !!appliedCoupon,
        coupon_code: appliedCoupon?.code || "",
        coupon_discount: appliedCoupon
          ? appliedCoupon.type === "percentage"
            ? ((state.cart?.subTotalWithTax || 0) * appliedCoupon.discount) /
              100
            : appliedCoupon.discount
          : 0,
        coupon_type: appliedCoupon?.type || "",
        currency: state.cart?.currencyCode || "INR",
        has_delivery_address: !!deliveryAddress,
      },
    });

    // Call the original handlePayment
    await handlePayment();
  };

  if (!state.cart || state.cart.lines.length === 0)
    return <div>Loading...</div>;

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {!paymentStarted && (
        <>
          <PaymentHeader />
          <div className="pt-20 pb-32">
            <div className="px-5 space-y-6">
              {deliveryAddress && (
                <DeliveryAddressCard address={deliveryAddress} />
              )}
              <OrderSummary
                cartLines={state.cart?.lines || []}
                summary={{
                  itemCount: state.cart.lines.reduce(
                    (sum, line) => sum + line.quantity,
                    0
                  ),
                  subtotal: state.cart.subTotalWithTax,
                  deliveryFee: state.cart.shippingWithTax,
                }}
                appliedCoupon={appliedCoupon}
                finalTotal={finalTotal}
              />
            </div>
          </div>
          <PaymentButton
            total={finalTotal}
            onPayment={handlePaymentWithTracking}
            isProcessing={isProcessing}
            paymentMethod="card"
          />
        </>
      )}

      {paymentStarted && isVerifying && (
        <div className="flex flex-col items-center justify-center h-screen space-y-4">
          <Spinner />
          <p className="text-lg font-medium text-gray-700">
            Verifying your payment, please wait...
          </p>
        </div>
      )}
      <div id="cf_checkout" />
    </div>
  );
};
